import { useState, useRef, useEffect } from 'react'
import { Transition, Field, Label } from '@headlessui/react'
import ChevDown from '../../assets/chevdown.svg?react'
import { IOption } from '../../types'
import { normalizeString } from '../../utils/stringUtils'

export const CUSTOM_OPTION_ID = '__custom__'

interface SearchComboBoxFieldProps {
  label?: string
  options: IOption[]
  placeholder: string
  readOnly?: boolean
  maxLength?: number
  value?: IOption | null
  onSelect?: (item: IOption | null) => void
  error?: boolean
  resetAfterSelect?: boolean
}

export const SearchComboBoxField = (props: SearchComboBoxFieldProps) => {
  const { label, options, placeholder, readOnly = false, maxLength, value, onSelect, error, resetAfterSelect = false } = props
  const [query, setQuery] = useState(value?.label || '')
  const [selected, setSelected] = useState<IOption | null>(value || null)
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [isFiltering, setIsFiltering] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!resetAfterSelect) {
      if (value?.id !== selected?.id || value?.label !== selected?.label) {
        setSelected(value || null)
        setQuery(value?.label || '')
      }
    }
  }, [value])

  const filteredItems = !isFiltering || query === ''
    ? options
    : options.filter((item) => normalizeString(item.label).toLowerCase().includes(normalizeString(query).toLowerCase()))

  const handleSelect = (item: IOption) => {
    if (resetAfterSelect) {
      setSelected(null)
      setQuery('')
      setIsOpen(false)
      setFocusedIndex(-1)
      onSelect?.(item)
    } else {
      setSelected(item)
      setQuery(item.label)
      setIsOpen(false)
      setFocusedIndex(-1)
      onSelect?.(item)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return

    setQuery(e.target.value)
    setIsOpen(true)
    setIsFiltering(true)
    setFocusedIndex(-1)

    const exactMatch = options.find(
      (item) => item.label.toLowerCase() === e.target.value.toLowerCase()
    )
    if (exactMatch) {
      setSelected(exactMatch)
      if (!resetAfterSelect) onSelect?.(exactMatch)
    } else if (e.target.value.trim()) {
      const customOption: IOption = { id: CUSTOM_OPTION_ID, label: e.target.value }
      setSelected(customOption)
      if (!resetAfterSelect) onSelect?.(customOption)
    } else {
      setSelected(null)
      if (!resetAfterSelect) onSelect?.(null)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (isOpen && focusedIndex >= 0 && focusedIndex < filteredItems.length) {
        handleSelect(filteredItems[focusedIndex])
      } else if (resetAfterSelect && query.trim()) {
        const exactMatch = options.find(
          (item) => item.label.toLowerCase() === query.trim().toLowerCase()
        )
        const resolved = exactMatch || { id: CUSTOM_OPTION_ID, label: query.trim() }
        setSelected(resolved)
        onSelect?.(resolved)
        setIsOpen(false)
        setFocusedIndex(-1)
        setQuery('')
      }
      return
    }

    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true)
      return
    }

    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex((prev) =>
          prev < filteredItems.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredItems.length - 1
        )
        break
      case 'Escape':
        setIsOpen(false)
        setFocusedIndex(-1)
        if (selected) {
          setQuery(selected.label)
        }
        inputRef.current?.blur()
        break
    }
  }

  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedEl = listRef.current.children[focusedIndex] as HTMLElement
      focusedEl?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setFocusedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <Field>
        {label && <Label className={'text-lg'}>{label}</Label>}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            className={`mt-3 block w-full appearance-none rounded-lg border-1 px-3 py-3 text-base focus:not-data-focus:outline-none [&:invalid]:text-placeholderText ${error ? 'border-red-500' : 'border-formBorder'
              }`}
            value={query}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            onBlur={(e) => {
              if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
                setIsOpen(false)
                setFocusedIndex(-1)
                if (query.trim() && !selected) {
                  const customOption: IOption = { id: CUSTOM_OPTION_ID, label: query.trim() }
                  setSelected(customOption)
                  onSelect?.(customOption)
                }
                if (!resetAfterSelect && selected) {
                  setQuery(selected.label)
                }
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            readOnly={readOnly}
            maxLength={maxLength}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-activedescendant={
              focusedIndex >= 0 ? `option-${filteredItems[focusedIndex]?.id}` : undefined
            }
          />
          <button
            type="button"
            className={'absolute inset-y-0 right-2 px-2.5'}
            onClick={() => {
              setIsFiltering(false)
              setIsOpen(!isOpen)
              inputRef.current?.focus()
            }}
            tabIndex={-1}
            aria-label="Toggle options"
          >
            <ChevDown width={16} height={16} />
          </button>
        </div>

        <Transition
          show={isOpen}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <ul
            ref={listRef}
            className="absolute mt-1 max-h-[400px] w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm z-10"
            role="listbox"
            onMouseDown={(e) => e.preventDefault()}
          >
            {filteredItems.length === 0 ? (
              <li className="relative cursor-default select-none px-4 py-2 text-gray-700">
                Nothing found.
              </li>
            ) : (
              filteredItems.map((item, index) => {
                const isFocused = focusedIndex === index
                return (
                  <li
                    key={item.id}
                    id={`option-${item.id}`}
                    role="option"
                    aria-selected={selected?.id === item.id}
                    className={`relative cursor-default select-none text-base py-2 px-4 ${isFocused ? 'bg-blue-100' : ''
                      }`}
                    onMouseEnter={() => setFocusedIndex(index)}
                    onMouseLeave={() => setFocusedIndex(-1)}
                    onClick={() => handleSelect(item)}
                  >
                    <span className="block truncate">
                      {item.label}
                    </span>
                  </li>
                )
              })
            )}
          </ul>
        </Transition>
      </Field>
    </div>
  )
}
